import { useState } from 'react';
import { Search } from 'lucide-react';
import { ApiError } from '../../../lib/api';
import { lookupAnafByCui } from '../../../lib/anafApi';
import type { IssuedInvoiceCustomerInput } from '../../../lib/issuedInvoicesApi';
import { useClients } from '../../../hooks/useClients';
import { useCountyOptions } from '../../../hooks/useIssuedInvoices';
import { Select } from '../../ui/Select';

export const EMPTY_CUSTOMER: IssuedInvoiceCustomerInput = {
  clientId: null,
  name: '',
  cui: '',
  regCom: '',
  vatPayer: false,
  street: '',
  city: '',
  countyCode: '',
  country: 'RO',
  email: '',
};

interface Props {
  value: IssuedInvoiceCustomerInput;
  onChange: (next: IssuedInvoiceCustomerInput) => void;
  disabled?: boolean;
}

const inputCls =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500';

/**
 * Customer block of the invoice form, SmartBill-style: type the CUI, hit
 * "Caută la ANAF", and the legal name, J number and structured address are
 * filled in; everything stays editable. Existing clients can be picked too.
 */
export function CustomerPicker({ value, onChange, disabled }: Props) {
  const [lookupState, setLookupState] = useState<{
    busy: boolean;
    message: string | null;
    error: boolean;
  }>({ busy: false, message: null, error: false });
  const counties = useCountyOptions();
  const clients = useClients({ active: 'true', limit: 200, sort: 'name' });

  function patch(p: Partial<IssuedInvoiceCustomerInput>) {
    onChange({ ...value, ...p });
  }

  async function lookup() {
    const cui = value.cui.trim();
    if (!cui) return;
    setLookupState({ busy: true, message: null, error: false });
    try {
      const result = await lookupAnafByCui(cui);
      const c = result.company;
      if (!c) {
        setLookupState({
          busy: false,
          message: `CUI-ul ${result.cui} nu există la ANAF.`,
          error: true,
        });
        return;
      }
      patch({
        cui: result.cui,
        name: c.name || value.name,
        regCom: c.regCom ?? value.regCom,
        vatPayer: c.vatPayer,
        street: c.street ?? c.address ?? value.street,
        city: c.city ?? value.city,
        countyCode: c.countyCode ?? value.countyCode,
      });
      setLookupState({
        busy: false,
        message:
          result.warning ??
          `Date preluate de la ANAF${c.vatPayer ? ' (plătitor de TVA)' : ' (neplătitor de TVA)'}.`,
        error: Boolean(result.warning),
      });
    } catch (err) {
      setLookupState({
        busy: false,
        message: err instanceof ApiError ? err.message : 'Căutarea la ANAF a eșuat.',
        error: true,
      });
    }
  }

  const clientOptions = (clients.data?.data ?? [])
    .filter((c) => c.cifCui)
    .map((c) => ({ value: c.id, label: `${c.name} · ${c.cifCui}` }));

  function pickClient(id: string) {
    const c = (clients.data?.data ?? []).find((x) => x.id === id);
    if (!c) return;
    patch({
      clientId: c.id,
      name: c.name,
      cui: (c.cifCui ?? '').replace(/^ro/i, ''),
      regCom: c.regCom ?? '',
      vatPayer: /^ro/i.test(c.cifCui ?? ''),
      email: c.email ?? '',
    });
    setLookupState({
      busy: false,
      message: 'Verifică adresa: apasă „Caută la ANAF” ca să o completezi automat.',
      error: false,
    });
  }

  const isBucharest = value.countyCode === 'RO-B';

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-gray-600">CUI / CIF client</label>
          <div className="flex gap-2">
            <input
              className={inputCls}
              placeholder="ex. 52908122"
              value={value.cui}
              disabled={disabled}
              onChange={(e) => patch({ cui: e.target.value, clientId: null })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void lookup();
                }
              }}
            />
            <button
              type="button"
              onClick={() => void lookup()}
              disabled={disabled || lookupState.busy || !value.cui.trim()}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
              <Search className="h-4 w-4" />
              {lookupState.busy ? 'Caut…' : 'Caută la ANAF'}
            </button>
          </div>
        </div>
        <div className="sm:w-72">
          <Select
            label="Sau alege un client existent"
            value={value.clientId ?? ''}
            onChange={pickClient}
            options={clientOptions}
            placeholder="Clienți cu CUI"
            searchable
            size="sm"
            width={288}
            disabled={disabled}
            loading={clients.isLoading}
          />
        </div>
      </div>
      {lookupState.message && (
        <p className={`text-xs ${lookupState.error ? 'text-amber-700' : 'text-green-700'}`}>
          {lookupState.message}
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium text-gray-600">Denumire client *</label>
          <input
            className={inputCls}
            value={value.name}
            disabled={disabled}
            onChange={(e) => patch({ name: e.target.value })}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Nr. Reg. Com.</label>
          <input
            className={inputCls}
            placeholder="J40/1234/2020"
            value={value.regCom ?? ''}
            disabled={disabled}
            onChange={(e) => patch({ regCom: e.target.value })}
          />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={value.vatPayer}
              disabled={disabled}
              onChange={(e) => patch({ vatPayer: e.target.checked })}
            />
            Plătitor de TVA
          </label>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium text-gray-600">Stradă și număr *</label>
          <input
            className={inputCls}
            value={value.street}
            disabled={disabled}
            onChange={(e) => patch({ street: e.target.value })}
          />
        </div>
        <div>
          <Select
            label="Județ *"
            value={value.countyCode}
            onChange={(code) => patch({ countyCode: code })}
            options={(counties.data ?? []).map((c) => ({
              value: c.code,
              label: `${c.name} (${c.code})`,
            }))}
            placeholder="Alege județul"
            searchable
            width={260}
            disabled={disabled}
            loading={counties.isLoading}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {isBucharest ? 'Sector (SECTOR1…SECTOR6) *' : 'Localitate *'}
          </label>
          <input
            className={inputCls}
            placeholder={isBucharest ? 'SECTOR6' : 'Cluj-Napoca'}
            value={value.city}
            disabled={disabled}
            onChange={(e) => patch({ city: e.target.value })}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Email client (opțional)
          </label>
          <input
            className={inputCls}
            type="email"
            value={value.email ?? ''}
            disabled={disabled}
            onChange={(e) => patch({ email: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}
